import { useMemo } from 'react';
import { useFetchConversations } from '../../hooks/data-fetching/useFetchConversations';
import useHasPermission from '../../hooks/useHasPermission';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { groupByKey, calculateTextingStatus } from '../../utils/texting';

/**
 * Will mainly calculate the texting column, similar to the ManageApplications table
 * ---
 * @notes
 *  - Review src/containers/ManageApplications/ApplicationsTable/ApplicationsTable_V2.hooks.js for reference
 * @param {*} param0
 * @returns
 */
const useParseResults = ({ results }) => {
  const { isTwoWayCommunicationActive } = useSelectedProperty();

  const twoWayRecipientIds = useMemo(
    () => results.map((r) => r.twoWayRecipientIds).flat(),
    [results],
  );

  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const showTextingColumn =
    isTwoWayCommunicationActive && hasCommunicationCreatePermission;

  const [conversations] = useFetchConversations(
    twoWayRecipientIds,
    showTextingColumn,
  );
  const conversationsDictionary = useMemo(() => {
    return groupByKey(conversations, (c) => c.recipient.customerId);
  }, [conversations]);

  const parsedResults = useMemo(() => {
    return results?.length
      ? results.map((result) => {
          const { notes } = result;
          let datesContacted;
          if (notes) {
            notes.forEach((note) => {
              if (note['reasons']?.[0]?.includes('Contacted')) {
                datesContacted = note['createdAt'];
              }
            });
          }
          const parsedResult = {
            ...result,
            datesContacted,
          };
          // Initialize default texting structure with empty values
          let householdConversationsStatus = {
            NEW: 0,
            UNRESOLVED: 0,
            RESOLVED: 0,
            TOTAL: 0,
          };

          // Only process if twoWayRecipientIds exists and is an array
          if (
            Array.isArray(result.twoWayRecipientIds) &&
            result.twoWayRecipientIds.length > 0
          ) {
            // Use the utility function to calculate texting status
            householdConversationsStatus = calculateTextingStatus(
              result.twoWayRecipientIds,
              conversationsDictionary,
            );
          }

          // Assign texting to parsedResult
          parsedResult.texting = householdConversationsStatus;

          return parsedResult;
        })
      : [];
  }, [results, conversationsDictionary]);

  return { parsedResults, showTextingColumn };
};

export default useParseResults;
